import axios from 'axios';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Endpoints } from '@/ts/store/Constants';
import FeedItemData from '@/ts/FeedItemData';
import CalendarEventData from '@/ts/CalendarEventData';
import { NotificationDataResponse } from '@/ts/HococoInterfaces';
import ErrorResponse from '@/ts/ErrorResponse';
import cloneDeep from 'lodash/cloneDeep';
import {isEmpty} from 'lodash';

@Module({name: 'Notifications', namespaced: true})
export default class Notifications extends VuexModule {
    public feedItems: FeedItemData[] = [];
    public feedItemsMap: { [index: number]: number } = {};
    public calendarEventsMap: { [index: number]: number} = {};
    public calendarEvents: CalendarEventData[] = [];
    public activeNotificationIndex: number = null;
    public editNotificationFormVisible: boolean = false;
    public cachedNotification: FeedItemData = null;
    public areEventFieldsVisible: boolean = false;
    public notificationSorted: boolean = false;
    public activeNotificationMedia: any = null;
    public dashboardNotificationsImages: any = [];
    public dashboardNotificationsPage: any = 1;
    public adminNotificationDataLoaded: boolean = false;
    public notificationActiveTab: string = 'all';
    public notificationIdForScroll: any = null;

    @Mutation
    public RESET_NOTIFICATIONS_STORE_STATE() {
        this.feedItems                    = [];
        this.feedItemsMap                 = {};
        this.calendarEventsMap            = {};
        this.calendarEvents               = [];
        this.activeNotificationIndex      = null;
        this.editNotificationFormVisible  = false;
        this.cachedNotification           = null;
        this.areEventFieldsVisible        = false;
        this.notificationSorted           = false;
        this.activeNotificationMedia      = null;
        this.dashboardNotificationsImages = [];
        this.dashboardNotificationsPage   = 1;
        this.adminNotificationDataLoaded  = false;
        this.notificationActiveTab        = 'all';
        this.notificationIdForScroll      = null;
    }

    get getAdminNotificationDataLoaded(): boolean {
        return this.adminNotificationDataLoaded;
    }

    get allNotifications() {
        return this.feedItems;
    }

    get getActiveNotification() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]];
    }

    get editNotificationMenuVisible(): boolean {
        return this.editNotificationFormVisible;
    }

    get eventFieldsVisibility(): boolean {
        return this.areEventFieldsVisible;
    }

    get getActiveNotificationId() {
        if (this.activeNotificationIndex != null) {
            return this.activeNotificationIndex;
        }

        return null;
    }

    get getNotificationTitle() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].title;
    }

    get getNotificationPublishedAt() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].publishedAt;
    }

    get getNotificationDescription() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].text;
    }

    get getNotificationEventId() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].event;
    }

    get getNotificationEventTitle() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventTitle;
    }

    get getNotificationEventDescription() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventText;
    }

    get getNotificationRecipients(): any {
        if (this.activeNotificationIndex === null) {
            return [];
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].recipients;
    }

    get getNotificationEventStartTime() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventStartTime;
    }

    get getNotificationEventEndTime() {
        if (this.activeNotificationIndex === null) {
            return null;
        }

        return this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventEndTime;
    }

    get getFeedItemData() {
        return this.feedItems;
    }

    get getCalendarEvent() {
        return (eventId: number): CalendarEventData | null => {
            if (!this.calendarEventsMap.hasOwnProperty(eventId)) {
                return null;
            }

            return this.calendarEvents[this.calendarEventsMap[eventId]];
        };
    }

    get getActiveNotificationMedia() {
        return this.activeNotificationMedia;
    }

    get getDashboardNotificationsPage() {
        return this.dashboardNotificationsPage;
    }

    get getNotificationMedia() {
        return (notificationId: number) => {
            return this.dashboardNotificationsImages.find((image: any) => image.notification_id === notificationId);
        };
    }

    get getNotificationRecipientIds() {
        const recipients =  cloneDeep(store.getters['Notifications/getNotificationRecipients']);

        if (isEmpty(recipients)) {
            return [];
        }

        const corporationIds = [store.getters['User/getUser'].corporationId];
        const projectIds     = recipients.projects.map((project: any) => project.id);
        const buildingIds    = recipients.buildings.map((group: any) => group.id);
        const propertyIds    = recipients.properties.map((unit: any) => unit.id);

        return {corporationIds, projectIds, buildingIds, propertyIds};
    }


    @Mutation
    public UPDATE_ORDER_NOTIFICATION(data: any) {
        const feedmeed = this.feedItems.find((obj: any) => obj.order ? obj.order.id === data.orderId : false);
        if(feedmeed && feedmeed.order) {
            feedmeed.order.status = data.status;
        }
    }

    get getNotificationIdForScroll() {
        return this.notificationIdForScroll;
    }

    get getNotificationActiveTab() {
        return this.notificationActiveTab;
    }

    @Action
    public INITIATE_NOTIFICATION() {
        store.commit(Mutations.INITIATE_NEW_NOTIFICATION);
    }

    @Action
    public FETCH_NOTIFICATIONS(data: any) {
        const count = typeof data.count !== 'undefined' ? data.count : null;

        axios.post(Endpoints.Notifications.NOTIFICATIONS_LIST, {
            page: store.getters['Notifications/getDashboardNotificationsPage'],
            type: data.type,
            interval: data.interval,
            count,
        })
            .then((response) => {
                if (response.data !== null) {
                    store.commit(Mutations.ADD_NOTIFICATIONS, response.data.data);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return Promise.resolve();
    }

    @Action
    public MARK_ACTIVE_NOTIFICATION_AS_SEEN(notificationId: number) {
        axios.put(Endpoints.Notifications.MARK_ACTIVE_NOTIFICATION_AS_SEEN(notificationId))
            .then((response) => {
                store.commit(Mutations.MARK_NOTIFICATION_AS_SEEN, notificationId);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ADMIN_NOTIFICATIONS() {
        store.commit(Mutations.SET_ADMIN_NOTIFICATION_DATA_LOAD_STATUS, false);

        axios.get(Endpoints.Admin.Notifications.NOTIFICATIONS)
            .then((response) => {
                if (response.data.data != null && response.data.data.length > 0) {
                    store.commit(Mutations.ADD_NOTIFICATIONS, response.data.data);
                } else {
                    store.commit(Mutations.SET_ADMIN_NOTIFICATION_DATA_LOAD_STATUS, true);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
        return;
    }

    @Action
    public POST_NOTIFICATIONS(data: any) {
        const notification = Notifications.state.feedItems[
                                Notifications.state.feedItemsMap[
                                    Notifications.state.activeNotificationIndex]];

        const actionTargets  = store.getters['Editor/getActionTarget'];
        const requestPayload = new FormData();

        requestPayload.append('title',  notification.title);
        requestPayload.append('publishedAt', notification.publishedAt);
        requestPayload.append('text', notification.text);
        requestPayload.append('channel', data.channel);
        requestPayload.append('recipients', JSON.stringify(data.recipients));
        requestPayload.append('sessionUUID', data.sessionUUID);
        requestPayload.append('mainImage', data.mainImage);

        axios.post(Endpoints.Admin.Notifications.NOTIFICATIONS, requestPayload, {
            headers: {
                'Content-Type': 'multipart-data',
            },
        })
            .then((response) => {
                store.commit(Mutations.ADD_NOTIFICATION, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_NOTIFICATION(data: any) {
        const notification = Notifications.state.feedItems[
                                Notifications.state.feedItemsMap[
                                    Notifications.state.activeNotificationIndex]];

        if (notification.dataEquals(Notifications.state.cachedNotification)) {
            return store.commit(Mutations.HIDE_EDIT_NOTIFICATION_FORM);
        }

        const actionTargets  = store.getters['Editor/getActionTarget'];
        const requestPayload = new FormData();

        requestPayload.append('title',  notification.title);
        requestPayload.append('publishedAt',  notification.publishedAt);
        requestPayload.append('text', notification.text);
        requestPayload.append('actionTarget', JSON.stringify(actionTargets.getTargets()));
        requestPayload.append('sessionUUID', data.sessionUUID);
        requestPayload.append('mainImage', data.mainImage);
        requestPayload.append('mainImageRemoved', data.mainImageRemoved);
        requestPayload.append('_method', 'PATCH');

        axios.post(Endpoints.Admin.Notifications.SINGLE_NOTIFICATION(notification.id), requestPayload, {
            headers: {
                'Content-Type': 'multipart-data',
            },
        })
            .then((response) => {
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_NOTIFICATION_MEDIA(notificationId: number) {
        axios.post(Endpoints.Admin.Notifications.NOTIFICATION_MEDIA(notificationId))
            .then((response) => {
                store.commit(Mutations.SET_ACTIVE_NOTIFICATION_MEDIA, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_NOTIFICATION_MEDIA_FOR_USER(notificationId: number) {
        axios.post(Endpoints.Notifications.USER_NOTIFICATION_MEDIA(notificationId))
            .then((response) => {
                store.commit(Mutations.ADD_DASHBOARD_NOTIFICATION_IMAGES, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_NOTIFICATION(data: any) {
        axios.delete(Endpoints.Admin.Notifications.SINGLE_NOTIFICATION(data.id))
            .then((response) => {
                if (response.data !== null && response.data.id !== null) {
                    store.commit(Mutations.DELETE_SINGLE_NOTIFICATION, data.id);
                    if (response.data.type === 'event') {
                        store.commit(Mutations.DELETE_SINGLE_EVENT, response.data.id);
                    } else if (response.data.type === 'document') {
                        store.commit(Mutations.REMOVE_CURRENT_DOCUMENT, response.data.id);
                    }
                }
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }


    @Mutation
    public ADD_DASHBOARD_NOTIFICATION_IMAGES(mediaData: any) {
        const checkImageExists = this.dashboardNotificationsImages.find((image: any) => image.notification_id === mediaData.notification_id);

        if (!checkImageExists) {
            this.dashboardNotificationsImages.push(mediaData);
        }
    }

    @Mutation
    public MARK_NOTIFICATION_AS_SEEN(notificationId: number ) {
        this.feedItems[this.feedItemsMap[notificationId]].read = true;
    }

    @Mutation
    public UPDATE_ACTIVE_NOTIFICATION(notificationId: number | null) {
        this.activeNotificationIndex = notificationId;
    }

    @Mutation
    public ADD_CALENDAR_EVENT(event: any) {
        let arrayIndex = null;

        if (!this.calendarEventsMap.hasOwnProperty(event.id)) {
            arrayIndex = this.calendarEvents.push(new CalendarEventData(
                event.id,
                event.title,
                null,
                null,
                event.event_time,
                event.isImportant,
            )) - 1;

            this.calendarEventsMap[event.id] = arrayIndex;
        }
    }

    @Mutation
    public ADD_NOTIFICATION(notification: NotificationDataResponse) {
        const arrayIndex = 0;

        this.feedItems.unshift(new FeedItemData(
            notification.id,
            notification.title,
            notification.authorId,
            notification.author,
            notification.isImportant,
            notification.text,
            notification.thumbnailId,
            notification.requiresResponse,
            null,
            notification.created_at,
            notification.updated_at,
            notification.type.id,
            notification.type.key,
            notification.attachments,
            notification.read,
            notification.recipients,
            notification.channel,
            notification.eventlog,
            notification.publishedAt,
            notification.order,
            notification.queueIsExecuted,
        ));

        if (notification.document !== null) {
            store.commit(Mutations.SET_ADMIN_DOCUMENT, notification.document);
            this.feedItems[arrayIndex].documentId = notification.document.id;
        }

        if (notification.event !== null) {
            store.commit(Mutations.ADD_EVENT, notification.event);
            this.feedItems[arrayIndex].eventId = notification.event.id;
            this.feedItems[arrayIndex].setEventData(
                notification.eventTitle,
                notification.eventText,
                notification.eventStartTime,
                notification.eventEndTime,
            );
        }

        this.feedItemsMap[notification.id] = arrayIndex;
        if (notification.document !== null) {
            this.feedItems[arrayIndex].documentId = notification.document.id;
        }

        if (typeof notification.hasMedia !== 'undefined' && notification.hasMedia !== null) {
            this.feedItems[arrayIndex].hasMedia = notification.hasMedia;
        }

        if (typeof notification.haveLike !== 'undefined' && notification.haveLike !== null) {
            this.feedItems[arrayIndex].haveLike = notification.haveLike;
        }

        if (typeof notification.likes !== 'undefined' && notification.likes !== null) {
            this.feedItems[arrayIndex].likes = notification.likes;
        }

        if (typeof notification.comments !== 'undefined' && notification.comments !== null) {
            this.feedItems[arrayIndex].comments = notification.comments;
        }

        if (typeof notification.likesRecipients !== 'undefined' && notification.likesRecipients !== null) {
            this.feedItems[arrayIndex].likesRecipients = notification.likesRecipients;
        }

        // fix item index map
        this.feedItemsMap = {};
        for (const [i, event] of this.feedItems.entries()) {
            if (i < this.feedItems.length - 1) {
                this.feedItemsMap[event.id] = i;
            }
        }

        this.feedItems.pop();
    }

    @Mutation
    public ADD_NOTIFICATION_ADMIN(notification: NotificationDataResponse) {
        let arrayIndex = null;

        if (notification.hasEvent === true) {
            store.commit(Mutations.ADD_CALENDAR_EVENT, notification.event);
        }

        if (!this.feedItemsMap.hasOwnProperty(notification.id)) {
            // this will probably be changed later
            arrayIndex = this.feedItems.push(new FeedItemData(
                    notification.id,
                    notification.title,
                    notification.authorId,
                    notification.author,
                    notification.isImportant,
                    notification.text,
                    notification.thumbnailId,
                    notification.requiresResponse,
                    null,
                    notification.created_at,
                    notification.updated_at,
                    notification.type.id,
                    notification.type.key,
                    notification.attachments,
                    notification.read,
                    notification.recipients,
                    notification.channel,
                    notification.eventlog,
                    notification.publishedAt,
                    notification.order,
                    notification.queueIsExecuted,
                )) - 1;

            if (notification.event !== null) {
                store.commit(Mutations.ADD_EVENT, notification.event);
                this.feedItems[arrayIndex].eventId = notification.event.id;
                this.feedItems[arrayIndex].setEventData(
                    notification.eventTitle,
                    notification.eventText,
                    notification.eventStartTime,
                    notification.eventEndTime,
                );
            }

            if (typeof notification.hasMedia !== 'undefined' && notification.hasMedia !== null) {
                this.feedItems[arrayIndex].hasMedia = notification.hasMedia;
            }

            if (notification.document !== null) {
                store.commit(Mutations.SET_ADMIN_DOCUMENT, notification.document);
                this.feedItems[arrayIndex].documentId = notification.document.id;
            }

            if (notification.document !== null) {
                store.commit(Mutations.SET_ADMIN_DOCUMENT, notification.document);
                this.feedItems[arrayIndex].documentId = notification.document.id;
            }

            this.feedItemsMap[notification.id] = arrayIndex;
        }
    }

    @Mutation
    public ADD_NOTIFICATIONS(notificationData: NotificationDataResponse[]) {
        const eventsToAdd = [];

        for (const notification of notificationData) {
            let arrayIndex = null;

            if (!this.feedItemsMap.hasOwnProperty(notification.id)) {
                // this will probably be changed later
                arrayIndex = this.feedItems.push(new FeedItemData(
                    notification.id,
                    notification.title,
                    notification.authorId,
                    notification.author,
                    notification.isImportant,
                    notification.text,
                    notification.thumbnailId,
                    notification.requiresResponse,
                    null,
                    notification.created_at,
                    notification.updated_at,
                    notification.type.id,
                    notification.type.key,
                    notification.attachments,
                    notification.read,
                    notification.recipients,
                    notification.channel,
                    notification.eventlog,
                    notification.publishedAt,
                    notification.order,
                    notification.queueIsExecuted,
                )) - 1;

                if (notification.event !== null) {
                    eventsToAdd.push(notification.event);
                    this.feedItems[arrayIndex].eventId = notification.event.id;
                    this.feedItems[arrayIndex].setEventData(
                        notification.event.title,
                        notification.event.body,
                        notification.event.startTime,
                        notification.event.endTime,
                    );
                }

                this.feedItemsMap[notification.id] = arrayIndex;
                if (notification.document) {
                    this.feedItems[arrayIndex].documentId = notification.document.id;
                }

                if (typeof notification.hasMedia !== 'undefined' && notification.hasMedia !== null) {
                    this.feedItems[arrayIndex].hasMedia = notification.hasMedia;
                }

                if (typeof notification.likes !== 'undefined' && notification.likes !== null) {
                    this.feedItems[arrayIndex].likes = notification.likes;
                }

                if (typeof notification.haveLike !== 'undefined' && notification.haveLike !== null) {
                    this.feedItems[arrayIndex].haveLike = notification.haveLike;
                }

                if (typeof notification.comments !== 'undefined' && notification.comments !== null) {
                    this.feedItems[arrayIndex].comments = notification.comments;
                }

                if (typeof notification.likesRecipients !== 'undefined' && notification.likesRecipients !== null) {
                    this.feedItems[arrayIndex].likesRecipients = notification.likesRecipients;
                }
            }
        }

        if (eventsToAdd.length > 0) {
            store.commit(Mutations.ADD_EVENTS, eventsToAdd);
        }

        store.commit(Mutations.SET_ADMIN_NOTIFICATION_DATA_LOAD_STATUS, true);
    }

    @Mutation
    public UPDATE_NOTIFICATION_TITLE(value: string): void {
        this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].title = value;
    }

    @Mutation
    public UPDATE_NOTIFICATION_PUBLISHED_AT(value: any): void {
        this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].publishedAt = value;
    }

    @Mutation
    public UPDATE_NOTIFICATION_DESCRIPTION(value: string): void {
        this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].text = value;
    }

    @Mutation
    public UPDATE_NOTIFICATION_EVENT_TITLE(value: string): void {
        this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventTitle = value;
    }

    @Mutation
    public UPDATE_NOTIFICATION_EVENT_DESCRIPTION(value: string): void {
        this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventText = value;
    }

    @Mutation
    public UPDATE_NOTIFICATION_EVENT_START_TIME(value: string): void {
        this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventStartTime = value;
    }

    @Mutation
    public UPDATE_NOTIFICATION_EVENT_END_TIME(value: string): void {
        this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].eventEndTime = value;
    }

    @Mutation
    public EDIT_ACTIVE_NOTIFICATION(): void {
        this.cachedNotification = this.feedItems[this.feedItemsMap[this.activeNotificationIndex]];
    }

    @Mutation
    public INITIATE_NEW_NOTIFICATION() {
        const activeIndex = this.feedItems.push(new FeedItemData(-1)) - 1;
        this.feedItemsMap[-1] = activeIndex;
        this.activeNotificationIndex = -1;
    }

    @Mutation
    public DELETE_SINGLE_NOTIFICATION(id: any): void {
        this.feedItems =  this.feedItems.filter((item: any) => {
            return item.id !== id;
        });
    }

    @Mutation
    public TOGGLE_EVENT_FIELDS() {
        if (this.areEventFieldsVisible === true) {
            this.areEventFieldsVisible = false;
            this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].hasEvent = false;
        } else {
            this.areEventFieldsVisible = true;
            this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].hasEvent = true;
        }
    }

    @Mutation
    public SHOW_EDIT_NOTIFICATION_FORM() {
        store.commit(Mutations.SET_CURRENT_FORM, 'edit-notification');
        this.editNotificationFormVisible = true;

        const activeNotification = this.feedItems[this.feedItemsMap[this.activeNotificationIndex]];

        if (activeNotification) {
            const eventId = this.feedItems[this.feedItemsMap[this.activeNotificationIndex]].event;

            if (eventId !== null) {
                this.areEventFieldsVisible = true;
            }
        }
    }

    @Mutation
    public HIDE_EDIT_NOTIFICATION_FORM() {
        this.editNotificationFormVisible = false;
    }

    @Mutation
    public SORT_ALL_NOTIFICATIONS() {
        this.notificationSorted = !this.notificationSorted;
        this.feedItems = this.feedItems.sort((a, b) => {
            return this.notificationSorted
                ? (new Date(a.createdAt.date) > new Date(b.createdAt.date)) ? 1 : -1
                : (new Date(a.createdAt.date) < new Date(b.createdAt.date)) ? 1 : -1;
        });

        this.feedItemsMap = {};
        for (const [i, event] of this.feedItems.entries()) {
            this.feedItemsMap[event.id] = i;
        }
    }

    @Mutation
    public SET_ACTIVE_NOTIFICATION_MEDIA(media: any) {
        this.activeNotificationMedia = media;
    }

    @Mutation
    public SET_ADMIN_NOTIFICATION_DATA_LOAD_STATUS(status: boolean) {
        this.adminNotificationDataLoaded = status;
    }

    @Mutation
    public SET_APP_DASHBOARD_NOTIFICATION_PAGE(page: any) {
        this.dashboardNotificationsPage = page;
    }

    @Mutation
    public SET_NOTIFICATION_ID_FOR_SCROLL(id: any) {
        this.notificationIdForScroll = id;
    }

    @Mutation
    public SET_NOTIFICATION_ACTIVE_TAB(tab: string) {
        this.notificationActiveTab = tab;
    }
}
